<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import CmsPagesAPI from 'dashboard/api/cmsPages';
import WebsiteLayout from '../components/WebsiteLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CmsPageForm from '../components/CmsPageForm.vue';
import CmsSectionForm from '../components/CmsSectionForm.vue';
import TablePagination from '../components/TablePagination.vue';
import ResponsiveTable from '../components/ResponsiveTable.vue';

const { t } = useI18n();

const pages = ref([]);
const loading = ref(false);
const showForm = ref(false);
const selectedPage = ref(null);
const expandedPageId = ref(null);
const showSectionForm = ref(false);
const selectedSection = ref(null);
const selectedPageForSection = ref(null);

// Header controls
const searchQuery = ref('');
const showFilterMenu = ref(false);
const showSortMenu = ref(false);
const filterPublished = ref(null);
const sortField = ref(null);
const sortOrder = ref('asc');

// Pagination
const currentPage = ref(1);
const itemsPerPage = ref(25);

const headerTitle = computed(() => t('WEBSITE.HEADER.CMS_TITLE'));

// Filtrage et tri (sans pagination)
const filteredAndSortedPages = computed(() => {
  let result = [...pages.value];

  // Recherche
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    result = result.filter(
      page =>
        page.title.toLowerCase().includes(query) ||
        page.slug.toLowerCase().includes(query)
    );
  }

  // Filtre published
  if (filterPublished.value !== null) {
    result = result.filter(page => page.published === filterPublished.value);
  }

  // Tri
  if (sortField.value) {
    result.sort((a, b) => {
      let aVal = a[sortField.value];
      let bVal = b[sortField.value];

      if (sortField.value === 'updated_at') {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }

      if (aVal < bVal) return sortOrder.value === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder.value === 'asc' ? 1 : -1;
      return 0;
    });
  }

  return result;
});

// Pagination
const startIndex = computed(() => (currentPage.value - 1) * itemsPerPage.value);

const endIndex = computed(() =>
  Math.min(
    startIndex.value + itemsPerPage.value,
    filteredAndSortedPages.value.length
  )
);

// Résultats paginés (utilisé pour l'affichage)
const paginatedPages = computed(() => {
  return filteredAndSortedPages.value.slice(startIndex.value, endIndex.value);
});

// États
const isEmpty = computed(() => !loading.value && pages.value.length === 0);

const hasNoSearchResults = computed(
  () =>
    !loading.value &&
    pages.value.length > 0 &&
    filteredAndSortedPages.value.length === 0 &&
    (searchQuery.value || filterPublished.value !== null)
);

// API calls
const fetchPages = async () => {
  loading.value = true;
  try {
    const response = await CmsPagesAPI.get();
    pages.value = response.data;
  } catch (error) {
    useAlert(t('WEBSITE.CMS.API.ERROR.FETCH_FAILED'));
  } finally {
    loading.value = false;
  }
};

const loadPageSections = async page => {
  try {
    const response = await CmsPagesAPI.show(page.id);
    const pageIndex = pages.value.findIndex(p => p.id === page.id);
    if (pageIndex !== -1) {
      pages.value[pageIndex] = {
        ...pages.value[pageIndex],
        sections: response.data.sections,
      };
    }
  } catch (error) {
    useAlert(t('WEBSITE.CMS.API.ERROR.FETCH_FAILED'));
  }
};

// Lifecycle
onMounted(() => {
  fetchPages();
});

// Reset pagination when filters change
watch([searchQuery, filterPublished, sortField, itemsPerPage], () => {
  currentPage.value = 1;
});

const openCreateForm = () => {
  selectedPage.value = null;
  showForm.value = true;
};

const openEditForm = page => {
  selectedPage.value = page;
  showForm.value = true;
};

const closeForm = () => {
  showForm.value = false;
  selectedPage.value = null;
};

const handleSave = async () => {
  await fetchPages();
  closeForm();
};

const togglePublish = async page => {
  try {
    if (page.published) {
      await CmsPagesAPI.unpublish(page.id);
      useAlert(t('WEBSITE.CMS.API.SUCCESS.PAGE_UNPUBLISHED'));
    } else {
      await CmsPagesAPI.publish(page.id);
      useAlert(t('WEBSITE.CMS.API.SUCCESS.PAGE_PUBLISHED'));
    }
    await fetchPages();
  } catch (error) {
    useAlert(t('WEBSITE.CMS.API.ERROR.PUBLISH_FAILED'));
  }
};

const deletePage = async page => {
  // TODO: Remplacer par un composant Dialog moderne
  // eslint-disable-next-line no-alert, no-restricted-globals
  if (!confirm(t('WEBSITE.CMS.CONFIRM.DELETE'))) return;

  try {
    await CmsPagesAPI.delete(page.id);
    useAlert(t('WEBSITE.CMS.API.SUCCESS.PAGE_DELETED'));
    await fetchPages();
  } catch (error) {
    useAlert(t('WEBSITE.CMS.API.ERROR.DELETE_FAILED'));
  }
};

const formatDate = dateString => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString();
};

// Fermer tous les menus sauf celui spécifié
const closeAllMenusExcept = exceptMenu => {
  if (exceptMenu !== 'filter') showFilterMenu.value = false;
  if (exceptMenu !== 'sort') showSortMenu.value = false;
};

// Gestion filtres et tri
const handleFilter = value => {
  filterPublished.value = value;
  showFilterMenu.value = false;
};

const handleSort = field => {
  if (sortField.value === field) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortField.value = field;
    sortOrder.value = 'asc';
  }
  showSortMenu.value = false;
};

const clearSearch = () => {
  searchQuery.value = '';
  filterPublished.value = null;
  sortField.value = null;
  sortOrder.value = 'asc';
  currentPage.value = 1;
};

const toggleExpand = async page => {
  if (expandedPageId.value === page.id) {
    expandedPageId.value = null;
  } else {
    expandedPageId.value = page.id;
    // Charger les détails de la page si pas déjà fait
    if (!page.sections) {
      await loadPageSections(page);
    }
  }
};

const openSectionForm = (page, section = null) => {
  selectedPageForSection.value = page;
  selectedSection.value = section;
  showSectionForm.value = true;
};

const closeSectionForm = () => {
  showSectionForm.value = false;
  selectedSection.value = null;
  selectedPageForSection.value = null;
};

const handleSectionSave = async () => {
  if (selectedPageForSection.value) {
    await loadPageSections(selectedPageForSection.value);
  }
  closeSectionForm();
};

const deleteSection = async (page, section) => {
  // TODO: Remplacer par un composant Dialog moderne
  // eslint-disable-next-line no-alert, no-restricted-globals
  if (!confirm(t('WEBSITE.CMS.CONFIRM.DELETE_SECTION'))) return;

  try {
    // On doit update la page sans cette section
    const sectionsToSave = page.sections
      .filter(s => s.id !== section.id)
      .map(s => ({ ...s }));

    // Marquer pour suppression
    sectionsToSave.push({ id: section.id, _destroy: true });

    await CmsPagesAPI.update(page.id, {
      cms_page: {
        cms_page_sections_attributes: sectionsToSave,
      },
    });

    useAlert(t('WEBSITE.CMS.API.SUCCESS.SECTION_DELETED'));
    await loadPageSections(page);
  } catch (error) {
    useAlert(t('WEBSITE.CMS.API.ERROR.DELETE_FAILED'));
  }
};
</script>

<template>
  <WebsiteLayout :header-title="headerTitle">
    <template #actions>
      <Button
        :label="t('WEBSITE.CMS.EMPTY_STATE.BUTTON_LABEL')"
        icon="i-lucide-plus"
        size="sm"
        @click="openCreateForm"
      />
    </template>

    <!-- Loading -->
    <div
      v-if="loading"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>

    <!-- Empty state -->
    <div
      v-else-if="isEmpty"
      class="flex flex-col items-center justify-center py-24 rounded-lg bg-gradient-to-br from-n-solid-1 to-n-slate-2 dark:to-n-slate-1 border-2 border-dashed border-n-weak"
    >
      <div
        class="mb-6 w-20 h-20 rounded-full bg-n-purple-2 dark:bg-n-purple-3 flex items-center justify-center"
      >
        <span class="i-lucide-file-text size-10 text-n-purple-10" />
      </div>
      <h3 class="text-lg font-semibold text-n-slate-12 mb-2">
        {{ t('WEBSITE.CMS.EMPTY_STATE.TITLE') }}
      </h3>
      <p class="text-sm text-center text-n-slate-10 max-w-md mb-6">
        {{ t('WEBSITE.CMS.EMPTY_STATE.SUBTITLE') }}
      </p>
      <Button
        :label="t('WEBSITE.CMS.EMPTY_STATE.BUTTON_LABEL')"
        icon="i-lucide-plus"
        size="sm"
        @click="openCreateForm"
      />
    </div>

    <!-- Header bar avec search, filtres, tri -->
    <div
      v-else-if="!isEmpty"
      class="mb-3 bg-n-solid-1 border border-n-weak/70 rounded-lg shadow-sm"
    >
      <div class="flex items-center gap-2 px-2.5 py-1.5">
        <!-- Search bar -->
        <div class="flex-1 max-w-sm">
          <Input
            v-model="searchQuery"
            type="search"
            :placeholder="t('WEBSITE.CMS.LIST.SEARCH_PLACEHOLDER')"
            :custom-input-class="[
              'h-8 [&:not(.focus)]:!border-transparent [&:not(.focus)]:!outline-transparent bg-n-alpha-2 dark:bg-n-solid-1 ltr:!pl-8 !py-1 rtl:!pr-8 !text-xs',
            ]"
          >
            <template #prefix>
              <Icon
                icon="i-lucide-search"
                class="absolute -translate-y-1/2 text-n-slate-11 size-3.5 top-1/2 ltr:left-2 rtl:right-2 pointer-events-none"
              />
            </template>
          </Input>
        </div>

        <!-- Séparateur vertical -->
        <div class="h-6 w-px bg-n-weak/40" />

        <!-- Filtres, Tri -->
        <div class="flex items-center gap-1.5">
          <!-- Filtre -->
          <div class="relative">
            <button
              class="px-2.5 py-1.5 bg-n-solid-1 border border-n-weak/50 text-n-slate-11 text-xs hover:bg-n-blue-2 hover:text-n-blue-11 hover:border-n-blue-6 transition-all flex items-center gap-1.5 rounded-lg font-medium"
              :class="{
                'bg-n-blue-3 text-n-blue-11 border-n-blue-6':
                  filterPublished !== null,
              }"
              @click="
                closeAllMenusExcept('filter');
                showFilterMenu = !showFilterMenu;
              "
            >
              <span class="i-lucide-filter size-3.5" />
              <span>{{ t('WEBSITE.CMS.LIST.FILTER') }}</span>
              <span
                v-if="filterPublished !== null"
                class="w-1.5 h-1.5 bg-n-blue-9 rounded-full animate-pulse"
              />
            </button>

            <div
              v-if="showFilterMenu"
              v-on-clickaway="() => (showFilterMenu = false)"
              class="absolute right-0 mt-1.5 w-56 bg-n-solid-1 border border-n-weak/60 shadow-2xl rounded-xl z-[100] py-2.5 overflow-hidden"
            >
              <button
                class="w-full px-4 py-3 text-left text-xs hover:bg-n-alpha-2 transition-all duration-150 flex items-center justify-between group"
                :class="{
                  'bg-n-blue-2 text-n-blue-11': filterPublished === null,
                }"
                @click="handleFilter(null)"
              >
                <span class="font-medium">{{
                  t('WEBSITE.CMS.LIST.ALL_PAGES')
                }}</span>
                <span
                  v-if="filterPublished === null"
                  class="i-lucide-check size-3.5 text-n-blue-11"
                />
              </button>
              <div class="h-px bg-n-weak/30 my-2 mx-3" />
              <button
                class="w-full px-4 py-3 text-left text-xs hover:bg-n-alpha-2 transition-all duration-150 flex items-center justify-between group"
                :class="{
                  'bg-n-teal-2 text-n-teal-11': filterPublished === true,
                }"
                @click="handleFilter(true)"
              >
                <span class="font-medium flex items-center gap-2.5">
                  <span
                    class="w-2 h-2 rounded-full bg-n-teal-9 group-hover:scale-110 transition-transform"
                  />
                  {{ t('WEBSITE.CMS.LIST.PUBLISHED') }}
                </span>
                <span
                  v-if="filterPublished === true"
                  class="i-lucide-check size-3.5 text-n-teal-11"
                />
              </button>
              <button
                class="w-full px-4 py-3 text-left text-xs hover:bg-n-alpha-2 transition-all duration-150 flex items-center justify-between group"
                :class="{
                  'bg-n-slate-2 text-n-slate-11': filterPublished === false,
                }"
                @click="handleFilter(false)"
              >
                <span class="font-medium flex items-center gap-2.5">
                  <span
                    class="w-2 h-2 rounded-full bg-n-slate-9 group-hover:scale-110 transition-transform"
                  />
                  {{ t('WEBSITE.CMS.LIST.DRAFT') }}
                </span>
                <span
                  v-if="filterPublished === false"
                  class="i-lucide-check size-3.5 text-n-slate-11"
                />
              </button>
            </div>
          </div>

          <!-- Tri -->
          <div class="relative">
            <button
              class="px-2.5 py-1.5 bg-n-solid-1 border border-n-weak/50 text-n-slate-11 text-xs hover:bg-n-blue-2 hover:text-n-blue-11 hover:border-n-blue-6 transition-all flex items-center gap-1.5 rounded-lg font-medium"
              :class="{
                'bg-n-blue-3 text-n-blue-11 border-n-blue-6': sortField,
              }"
              @click="
                closeAllMenusExcept('sort');
                showSortMenu = !showSortMenu;
              "
            >
              <span class="i-lucide-arrow-up-down size-3.5" />
              <span>{{ t('WEBSITE.CMS.LIST.SORT') }}</span>
              <span
                v-if="sortField"
                class="w-1.5 h-1.5 bg-n-blue-9 rounded-full animate-pulse"
              />
            </button>

            <div
              v-if="showSortMenu"
              v-on-clickaway="() => (showSortMenu = false)"
              class="absolute right-0 mt-1.5 w-60 bg-n-solid-1 border border-n-weak/60 shadow-2xl rounded-xl z-[100] py-2.5 overflow-hidden"
            >
              <button
                class="w-full px-4 py-3 text-left text-xs hover:bg-n-alpha-2 transition-all duration-150 flex items-center justify-between group"
                :class="{
                  'bg-n-blue-2 text-n-blue-11': sortField === 'title',
                }"
                @click="handleSort('title')"
              >
                <span class="flex items-center gap-2.5 font-medium">
                  <span
                    class="i-lucide-type size-3.5 group-hover:scale-110 transition-transform"
                  />
                  <span>{{ t('WEBSITE.CMS.LIST.SORT_TITLE') }}</span>
                </span>
                <span
                  v-if="sortField === 'title'"
                  :class="
                    sortOrder === 'asc'
                      ? 'i-lucide-arrow-up'
                      : 'i-lucide-arrow-down'
                  "
                  class="size-3.5 text-n-blue-11"
                />
              </button>
              <button
                class="w-full px-4 py-3 text-left text-xs hover:bg-n-alpha-2 transition-all duration-150 flex items-center justify-between group"
                :class="{
                  'bg-n-blue-2 text-n-blue-11': sortField === 'updated_at',
                }"
                @click="handleSort('updated_at')"
              >
                <span class="flex items-center gap-2.5 font-medium">
                  <span
                    class="i-lucide-calendar size-3.5 group-hover:scale-110 transition-transform"
                  />
                  <span>{{ t('WEBSITE.CMS.LIST.SORT_DATE') }}</span>
                </span>
                <span
                  v-if="sortField === 'updated_at'"
                  :class="
                    sortOrder === 'asc'
                      ? 'i-lucide-arrow-up'
                      : 'i-lucide-arrow-down'
                  "
                  class="size-3.5 text-n-blue-11"
                />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- No search results -->
    <div
      v-if="hasNoSearchResults"
      class="flex flex-col items-center justify-center py-16 bg-n-solid-1 border border-n-weak/70 rounded-lg"
    >
      <span class="i-lucide-search-x mb-4 text-n-slate-9 size-12" />
      <h3 class="text-base font-semibold text-center text-n-slate-12 mb-2">
        {{ t('WEBSITE.CMS.NO_RESULTS.TITLE') }}
      </h3>
      <p class="text-sm text-center text-n-slate-10 max-w-md mb-6">
        {{ t('WEBSITE.CMS.NO_RESULTS.SUBTITLE') }}
      </p>
      <Button
        :label="t('WEBSITE.CMS.NO_RESULTS.CLEAR_BUTTON')"
        icon="i-lucide-x"
        size="sm"
        variant="outline"
        color="slate"
        @click="clearSearch"
      />
    </div>

    <!-- Pages list -->
    <div
      v-else-if="!isEmpty && !hasNoSearchResults"
      class="overflow-hidden border rounded-lg border-n-weak/70 bg-n-solid-1 shadow-sm min-h-[400px]"
    >
      <ResponsiveTable>
        <table class="w-full">
          <thead class="bg-n-alpha-1/50 border-b border-n-weak/60">
            <tr>
              <th
                class="px-5 py-3 text-xs font-semibold text-left tracking-wide text-n-slate-11"
              >
                <div class="flex items-center gap-1.5">
                  <span class="i-lucide-chevron-right size-3.5 opacity-30" />
                  <span>{{ t('WEBSITE.CMS.LIST.TITLE') }}</span>
                </div>
              </th>
              <th
                class="px-5 py-3 text-xs font-semibold text-left tracking-wide text-n-slate-11"
              >
                <div class="flex items-center gap-1.5">
                  <span class="i-lucide-link size-3.5 opacity-40" />
                  <span>{{ t('WEBSITE.CMS.LIST.SLUG') }}</span>
                </div>
              </th>
              <th
                class="px-5 py-3 text-xs font-semibold text-left tracking-wide text-n-slate-11"
              >
                <div class="flex items-center gap-1.5">
                  <span class="i-lucide-activity size-3.5 opacity-40" />
                  <span>{{ t('WEBSITE.CMS.LIST.STATUS') }}</span>
                </div>
              </th>
              <th
                class="px-5 py-3 text-xs font-semibold text-left tracking-wide text-n-slate-11"
              >
                <div class="flex items-center gap-1.5">
                  <span class="i-lucide-layout size-3.5 opacity-40" />
                  <span>{{ t('WEBSITE.CMS.LIST.SECTIONS') }}</span>
                </div>
              </th>
              <th
                class="px-5 py-3 text-xs font-semibold text-left tracking-wide text-n-slate-11"
              >
                <div class="flex items-center gap-1.5">
                  <span class="i-lucide-calendar size-3.5 opacity-40" />
                  <span>{{ t('WEBSITE.CMS.LIST.UPDATED') }}</span>
                </div>
              </th>
              <th
                class="sticky right-0 px-5 py-3 text-xs font-semibold text-right tracking-wide text-n-slate-11 bg-n-alpha-1/50 backdrop-blur-sm shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.1)]"
              >
                <div class="flex items-center justify-end gap-1.5">
                  <span>{{ t('WEBSITE.CMS.LIST.ACTIONS') }}</span>
                  <span class="i-lucide-more-horizontal size-3.5 opacity-30" />
                </div>
              </th>
            </tr>
          </thead>
          <tbody class="bg-n-solid-1">
            <template v-for="page in paginatedPages" :key="page.id">
              <!-- Page row -->
              <tr
                class="transition-all duration-150 ease-out border-t border-n-weak/40 group cursor-pointer"
                :class="[
                  expandedPageId === page.id
                    ? 'bg-n-blue-3/50'
                    : 'bg-transparent hover:bg-n-blue-2/50',
                ]"
                @click="toggleExpand(page)"
              >
                <td class="px-5 py-3">
                  <div class="flex items-center gap-2.5">
                    <div
                      class="transition-transform duration-400 ease-out"
                      :class="{ 'rotate-90': expandedPageId === page.id }"
                    >
                      <span
                        class="i-lucide-chevron-right size-4 text-n-slate-10"
                      />
                    </div>
                    <span class="text-sm font-medium text-n-slate-12">
                      {{ page.title }}
                    </span>
                  </div>
                </td>
                <td class="px-5 py-3 text-sm text-n-slate-10">
                  /{{ page.slug }}
                </td>
                <td class="px-5 py-3">
                  <div
                    class="inline-flex items-center gap-1.5 px-2 py-0.5 text-[11px] font-medium rounded-full border whitespace-nowrap transition-all duration-200"
                    :class="[
                      page.published
                        ? 'bg-n-teal-2 text-n-teal-11 border-n-teal-6'
                        : 'bg-n-slate-2 text-n-slate-11 border-n-slate-6',
                    ]"
                  >
                    <div
                      class="w-1.5 h-1.5 rounded-full"
                      :class="[
                        page.published
                          ? 'bg-n-teal-9 animate-pulse'
                          : 'bg-n-slate-9',
                      ]"
                    />
                    {{
                      page.published
                        ? t('WEBSITE.CMS.LIST.PUBLISHED')
                        : t('WEBSITE.CMS.LIST.DRAFT')
                    }}
                  </div>
                </td>
                <td class="px-5 py-3 text-sm text-n-slate-10">
                  {{ page.sections_count || 0 }}
                </td>
                <td class="px-5 py-3 text-sm text-n-slate-10">
                  {{ formatDate(page.updated_at) }}
                </td>
                <td
                  class="sticky right-0 px-5 py-3 bg-n-solid-1 backdrop-blur-sm shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.1)]"
                >
                  <div class="flex justify-end gap-2">
                    <button
                      class="flex items-center justify-center w-8 h-8 rounded-md bg-n-blue-3 hover:bg-n-blue-4 text-n-blue-11 hover:text-n-blue-12 border border-n-blue-6 hover:border-n-blue-7 transition-all duration-200 shadow-sm hover:shadow-md"
                      :title="t('WEBSITE.CMS.SECTION.EDIT')"
                      @click.stop="openEditForm(page)"
                    >
                      <span class="i-lucide-edit size-4" />
                    </button>
                    <button
                      class="flex items-center justify-center w-8 h-8 rounded-md transition-all duration-200 shadow-sm hover:shadow-md border"
                      :class="[
                        page.published
                          ? 'bg-n-orange-3 hover:bg-n-orange-4 text-n-orange-11 hover:text-n-orange-12 border-n-orange-6 hover:border-n-orange-7'
                          : 'bg-n-teal-3 hover:bg-n-teal-4 text-n-teal-11 hover:text-n-teal-12 border-n-teal-6 hover:border-n-teal-7',
                      ]"
                      :title="
                        page.published
                          ? t('WEBSITE.CMS.FORM.UNPUBLISH')
                          : t('WEBSITE.CMS.FORM.PUBLISH')
                      "
                      @click.stop="togglePublish(page)"
                    >
                      <span
                        :class="
                          page.published
                            ? 'i-lucide-eye-off size-4'
                            : 'i-lucide-eye size-4'
                        "
                      />
                    </button>
                    <button
                      class="flex items-center justify-center w-8 h-8 rounded-md bg-n-ruby-3 hover:bg-n-ruby-4 text-n-ruby-11 hover:text-n-ruby-12 border border-n-ruby-6 hover:border-n-ruby-7 transition-all duration-200 shadow-sm hover:shadow-md"
                      :title="t('WEBSITE.CMS.FORM.DELETE')"
                      @click.stop="deletePage(page)"
                    >
                      <span class="i-lucide-trash-2 size-4" />
                    </button>
                  </div>
                </td>
              </tr>

              <!-- Sections expandables -->
              <tr
                v-if="expandedPageId === page.id"
                class="bg-n-alpha-2/40 border-t border-n-weak/50"
              >
                <td colspan="6" class="px-5 py-5">
                  <div class="ml-8">
                    <div class="flex items-center justify-between mb-3">
                      <h4 class="text-sm font-semibold text-n-slate-12">
                        {{ t('WEBSITE.CMS.FORM.SECTIONS') }}
                      </h4>
                      <Button
                        :label="t('WEBSITE.CMS.FORM.ADD_SECTION')"
                        icon="i-lucide-plus"
                        size="sm"
                        color="slate"
                        variant="outline"
                        @click="openSectionForm(page)"
                      />
                    </div>

                    <div
                      v-if="!page.sections || page.sections.length === 0"
                      class="py-8 text-center text-sm text-n-slate-10"
                    >
                      {{ t('WEBSITE.CMS.SECTION.NO_SECTIONS') }}
                    </div>

                    <div v-else class="space-y-2">
                      <div
                        v-for="section in page.sections"
                        :key="section.id"
                        class="flex items-center justify-between p-3 rounded-lg border border-n-weak bg-n-solid-2 hover:border-n-slate-6 transition-all group/section"
                      >
                        <div class="flex items-center gap-3 flex-1 min-w-0">
                          <span
                            class="i-lucide-layout size-4 text-n-slate-10"
                          />
                          <div class="flex-1 min-w-0">
                            <div class="text-sm font-medium text-n-slate-12">
                              {{ section.name }}
                            </div>
                            <div
                              class="flex items-center gap-2 text-xs text-n-slate-10"
                            >
                              <span>{{ section.identifier }}</span>
                              <span>•</span>
                              <span>{{
                                t(
                                  `WEBSITE.CMS.SECTION.TYPES.${section.content_type}`
                                )
                              }}</span>
                              <span
                                v-if="!section.visible"
                                class="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-n-slate-3 text-n-slate-11"
                              >
                                {{ t('WEBSITE.CMS.SECTION.HIDDEN') }}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div class="flex gap-2">
                          <button
                            class="flex items-center justify-center w-8 h-8 rounded-md bg-n-blue-3 hover:bg-n-blue-4 text-n-blue-11 hover:text-n-blue-12 border border-n-blue-6 hover:border-n-blue-7 transition-all duration-200 shadow-sm hover:shadow-md"
                            :title="t('WEBSITE.CMS.SECTION.EDIT')"
                            @click="openSectionForm(page, section)"
                          >
                            <span class="i-lucide-edit size-4" />
                          </button>
                          <button
                            class="flex items-center justify-center w-8 h-8 rounded-md bg-n-ruby-3 hover:bg-n-ruby-4 text-n-ruby-11 hover:text-n-ruby-12 border border-n-ruby-6 hover:border-n-ruby-7 transition-all duration-200 shadow-sm hover:shadow-md"
                            :title="t('WEBSITE.CMS.SECTION.REMOVE')"
                            @click="deleteSection(page, section)"
                          >
                            <span class="i-lucide-trash-2 size-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </ResponsiveTable>
    </div>

    <!-- Pagination Footer -->
    <div
      v-if="
        !isEmpty && !hasNoSearchResults && filteredAndSortedPages.length > 0
      "
      class="mt-6"
    >
      <TablePagination
        :current-page="currentPage"
        :total-items="filteredAndSortedPages.length"
        :items-per-page="itemsPerPage"
        @update:current-page="currentPage = $event"
        @update:items-per-page="itemsPerPage = $event"
      />
    </div>

    <!-- Page Form Dialog -->
    <CmsPageForm
      v-if="showForm"
      :page="selectedPage"
      @close="closeForm"
      @save="handleSave"
    />

    <!-- Section Form Dialog -->
    <CmsSectionForm
      v-if="showSectionForm"
      :page="selectedPageForSection"
      :section="selectedSection"
      @close="closeSectionForm"
      @save="handleSectionSave"
    />
  </WebsiteLayout>
</template>
